import { PureComponent } from 'react';
import { layer, source, feature } from 'react-openlayers';
import { fromLonLat } from 'ol/proj';
import EventBus from '../../../EventBus';
import { MAP_EVENTS } from '../../../mapEvents';
import { MISSION_EVENTS } from '../../../missionEvents';
import { addAlpha, generateColor } from '../../../utils';
import type { CoreGridSegment } from '../../../services/zones/AutoCorePlacer';

const METERS_PER_DEG_LAT = 111_320;
/** Draw the grid below cores/centroids (zIndex 4) but above zone fills. */
const GRID_Z_INDEX = 2;
/** Fill opacity over the basemap. */
const GRID_FILL_ALPHA = 0.45;

interface CorePlacementGridLayerState {
  features: JSX.Element[];
}

/**
 * Technician-only debug overlay: draws the equal-area kernel's rasterized grid,
 * each sample colored by the cell (core) it's assigned to. Self-contained —
 * owns its EventBus subscriptions:
 *   - MAP_EVENTS.CORE_GRID_UPDATED — payload is the per-segment grid to draw,
 *     or `null` to clear (fired by the core-placement tuning panel).
 *   - MISSION_EVENTS.CREATED_OR_DELETED — clear on mission swap (avoid stale grid).
 *
 * The grid is built in a local axis-aligned frame, so every grid row is a line of
 * constant latitude. We exploit that to run-length-merge each row into a few
 * seamless rectangles (one per contiguous same-cell run) instead of thousands of
 * per-sample squares — keeps the overlay crisp and the feature count low.
 */
export default class CorePlacementGridLayer extends PureComponent<object, CorePlacementGridLayerState> {
  state: CorePlacementGridLayerState = { features: [] };

  componentDidMount() {
    EventBus.on(MAP_EVENTS.CORE_GRID_UPDATED, this.onGridUpdated);
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.clear);
  }

  componentWillUnmount() {
    EventBus.remove(MAP_EVENTS.CORE_GRID_UPDATED, this.onGridUpdated);
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.clear);
  }

  private clear = () => {
    if (this.state.features.length > 0) {
      this.setState({ features: [] });
    }
  };

  private onGridUpdated = (grids: CoreGridSegment[] | null) => {
    if (!grids || grids.length === 0) {
      this.clear();

      return;
    }

    const features: JSX.Element[] = [];
    let key = 0;

    for (const g of grids) {
      key = this.appendSegmentRects(g, features, key);
    }

    this.setState({ features });
  };

  /**
   * Run-length-merge one segment's grid into rectangles: group samples by row
   * (constant latitude), then within each row emit one rectangle per contiguous
   * run of same-cell samples.
   */
  private appendSegmentRects(g: CoreGridSegment, out: JSX.Element[], startKey: number): number {
    const step = g.stepMeters;

    if (step <= 0 || g.samples.length === 0) {
      return startKey;
    }

    const dLat = step / METERS_PER_DEG_LAT;

    let minLat = Infinity;

    for (const [, lat] of g.samples) {
      if (lat < minLat) {
        minLat = lat;
      }
    }

    // Group sample indices by grid row.
    const rows = new Map<number, number[]>();

    for (let i = 0; i < g.samples.length; i++) {
      const rowKey = Math.round((g.samples[i][1] - minLat) / dLat);
      const arr = rows.get(rowKey);

      if (arr) {
        arr.push(i);
      } else {
        rows.set(rowKey, [i]);
      }
    }

    let key = startKey;

    for (const idxs of rows.values()) {
      idxs.sort((a, b) => g.samples[a][0] - g.samples[b][0]);
      const lat = g.samples[idxs[0]][1];
      const dLon = step / (METERS_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180));

      let runStart = 0;

      for (let j = 1; j <= idxs.length; j++) {
        const prev = idxs[j - 1];
        const cur = j < idxs.length ? idxs[j] : -1;
        const contiguous =
          cur >= 0 &&
          g.assign[cur] === g.assign[prev] &&
          Math.abs(g.samples[cur][0] - g.samples[prev][0] - dLon) < dLon * 0.5;

        if (!contiguous) {
          const first = idxs[runStart];
          const lastInRun = idxs[j - 1];
          out.push(
            this.rect(
              g.samples[first][0] - dLon / 2,
              g.samples[lastInRun][0] + dLon / 2,
              lat - dLat / 2,
              lat + dLat / 2,
              g.colorSeedBase + g.assign[first],
              key++,
            ),
          );
          runStart = j;
        }
      }
    }

    return key;
  }

  /** A single colored rectangle (EPSG:3857 ring) spanning [lon0,lon1] × [lat0,lat1]. */
  private rect(lon0: number, lon1: number, lat0: number, lat1: number, seed: number, key: number): JSX.Element {
    const ring = [
      fromLonLat([lon0, lat0]),
      fromLonLat([lon1, lat0]),
      fromLonLat([lon1, lat1]),
      fromLonLat([lon0, lat1]),
      fromLonLat([lon0, lat0]),
    ];

    return (
      <feature.PolygonReact
        key={key}
        // @ts-ignore react-openlayers PolygonReact takes raw 3857 ring coordinates
        coordinates={[ring]}
        fillOptions={{ color: addAlpha(generateColor(String(seed)), GRID_FILL_ALPHA) }}
        strokeOptions={{ color: 'rgba(0, 0, 0, 0)', width: 0 }}
      />
    );
  }

  render() {
    return (
      // @ts-ignore react-openlayers layer typing
      <layer.VectorImage zIndex={GRID_Z_INDEX}>
        {/* @ts-ignore react-openlayers source typing */}
        <source.VectorSourceReact>{this.state.features}</source.VectorSourceReact>
      </layer.VectorImage>
    );
  }
}
